import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { Subscription } from 'rxjs';
import { RegBlobDataTypeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationService } from 'src/app/core/services/registration.service';

@Component({
  selector: 'app-document-upload-form',
  templateUrl: './document-upload-form.component.html',
  styleUrls: ['./document-upload-form.component.scss']
})
export class DocumentUploadFormComponent implements OnInit {
  @Input() regBlobDataTypeId: number;
  @Input() isFromMyAccount: boolean;
  @Input() regId?: number;
  @Output() updateUploadStatus = new EventEmitter<void>();

  @ViewChild('inputFile') fileInputVariable: ElementRef;

  scannedDocumentImg: string;
  uploadedFile: File;
  fileExtensionError: boolean;
  fileSizeError: boolean;
  accountSubscription: Subscription;
  isUploaded: boolean;
  error: boolean;

  uploadForm = new FormGroup({
    scannedDocument: new FormControl(null, [Validators.required])
  });

  get f() {
    return this.uploadForm.controls;
  }

  constructor(
    private registrationService: RegistrationService,
    private loadingService: LoadingService,
    private toastService: HotToastService,
  ) { }

  public get RegBlobDataTypeEnum() {
    return RegBlobDataTypeEnum;
  }

  ngOnInit(): void {
    this.regId = this.regId ? this.regId : 0;
  }

  onChangeFile(event) {
    this.uploadedFile = event.target.files[0];
    if (this.uploadedFile.type !== "image/jpeg") {
      this.fileExtensionError = true;
    }
    else if (this.uploadedFile.size > 1024 * 1024 * 2) {
      this.fileSizeError = true;
    }
    else {
      this.fileSizeError = false;
      this.fileExtensionError = false;
      const reader = new FileReader();
      reader.readAsDataURL(this.uploadedFile);
      reader.onloadend = () => {
        this.scannedDocumentImg = reader.result as string;
        if (this.regBlobDataTypeId === RegBlobDataTypeEnum.PictureFromRegistration) {
          this.resizePhoto(640, 480);
        }
      };
    }
  }

  resizePhoto(targetWidth: number, targetHeight: number) {
    const image = new Image();
    image.src = this.scannedDocumentImg;
    image.onload = () => {
      let originalHeight = image.naturalHeight;
      let originalWidth = image.naturalWidth;
      const canvas = document.createElement('canvas');
      canvas.height = targetHeight;
      canvas.width = targetWidth;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      let scale = 1;
      if ((targetWidth / originalWidth) !== 1 && (targetHeight / originalHeight) !== 1) {
        scale = Math.min(targetWidth / originalWidth, targetHeight / originalHeight);
      }
      originalWidth = originalWidth * scale;
      originalHeight = originalHeight * scale;
      const xoffset = (canvas.width - originalWidth) / 2;
      const yoffset = Math.max((canvas.height - originalHeight) / 2, 0);
      ctx.drawImage(image, xoffset, yoffset, originalWidth, originalHeight);
      const data = canvas.toDataURL('image/jpeg');
      this.scannedDocumentImg = data;
      const arr = data.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      this.uploadedFile = new File([u8arr], "myFile", { type: mime });
    };
  }

  reset() {
    this.fileInputVariable.nativeElement.value = null;
    this.scannedDocumentImg = null;
    this.isUploaded = false;
  }

  uploadFile() {
    const formData = new FormData();
    formData.append("file", this.uploadedFile, "myFile");
    this.loadingService.show();
    this.registrationService.uploadDocument(this.regId, formData, this.regBlobDataTypeId)
      .subscribe(
        () => {
          this.isUploaded = true;
          this.updateUploadStatus.emit();
        },
        (error: HttpErrorResponse) => {
          if (error.status !== 401) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
    this.loadingService.hide();
  }

}
